import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Box,
  Button,
  Menu as MuiMenu,
  MenuItem,
  Divider,
  Chip,
  Stack,
  useScrollTrigger,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import {
  MenuOutlined,
  ExitToApp,
  Person,
  Dashboard as DashboardIcon,
  AdminPanelSettings,
  HelpOutline,
  SystemUpdateAlt,
  DesktopWindows,
  DarkMode,
  LightMode,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import colors from '../design-system/colors';
import spacing from '../design-system/spacing';
import shadows from '../design-system/shadows';
import { useLicenseTier } from '../hooks/useLicenseTier';
import { usePageTourContext } from './onboarding/PageTourProvider';
import HelpModal, { resolveHelpPageKey } from './onboarding/HelpModal';
import TutorialHelpLink from './tutorials/TutorialHelpLink';

const StyledAppBar = styled(AppBar)(({ elevation }) => ({
  backgroundColor: colors.primary[900],
  boxShadow: elevation ? shadows.navbar : 'none',
  borderRadius: 0,
  transition: 'all 0.3s ease',
}));

const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: spacing[2],
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
});

const UserMenuItem = styled(MenuItem)({
  padding: `${spacing[2]}px ${spacing[3]}px`,
  '&:hover': {
    backgroundColor: colors.primary[50],
  },
});

const getRoleLabel = (role, t) => t(`common:roles.${role}`, { defaultValue: t('common:roles.user') });

const ModernHeader = ({
  isAuthenticated,
  onLogin,
  onLogout,
  toggleMenu,
  usuario,
  themeMode = 'light',
  onToggleTheme = () => {},
  onOpenWelcomeTour,
}) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);

  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const pendingTourAction = React.useRef(null);
  const { t, i18n } = useTranslation(['navigation', 'common']);
  const { currentPage: registeredPage, hasSteps, startTour } = usePageTourContext();

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 10,
  });

  const resolvedHelpPage = resolveHelpPageKey(location.pathname, registeredPage);
  const hasPageTour = registeredPage === resolvedHelpPage && hasSteps;

  const userId = usuario?.oid || null;
  const userRole = usuario?.rol || 'usuario';
  const tier = useLicenseTier(userId, userRole);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };



  const handleHelpModalOpen = () => {
    setHelpModalOpen(true);
  };

  const handleHelpModalClose = () => {
    setHelpModalOpen(false);
  };

  const handleHelpModalExited = () => {
    if (pendingTourAction.current) {
      const action = pendingTourAction.current;
      pendingTourAction.current = null;
      setTimeout(() => {
        const rootEl = document.getElementById('root');
        if (rootEl?.getAttribute('aria-hidden')) {
          rootEl.removeAttribute('aria-hidden');
        }
        action();
      }, 150);
    }
  };

  const handleStartTour = () => {
    pendingTourAction.current = () => {
      if (hasPageTour) {
        startTour(resolvedHelpPage);
      } else {
        onOpenWelcomeTour?.();
      }
    };
    setHelpModalOpen(false);
  };

  const getUserInitials = (nombre) => {
    if (!nombre) return 'U';
    const parts = nombre.split(' ');
    return parts.map((p) => p[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <StyledAppBar position="sticky" elevation={trigger ? 4 : 0}>
      <Toolbar sx={{ gap: 2, borderRadius: 0 }}>
        {isAuthenticated && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={toggleMenu}
            aria-label={t('header.menuAriaLabel')}
            sx={{
              mr: 1,
              '&:hover': {
                backgroundColor: colors.primary[800],
              },
            }}
          >
            <MenuOutlined />
          </IconButton>
        )}

        <LogoContainer component={Link} data-tour="header-brand" to={isAuthenticated ? '/dashboard' : '/'}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/favicon.svg?v=20261027`}
            alt=""
            aria-hidden="true"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              display: 'block',
            }}
          />
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.neutral.white,
                letterSpacing: 0.5,
              }}
            >
              {t('header.appName')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.primary[200],
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              {t('header.appSubtitle')}
            </Typography>
          </Box>
        </LogoContainer>

        <Box sx={{ flexGrow: 1 }} />

        {isAuthenticated ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TutorialHelpLink compact />
            <IconButton
              color="inherit"
              component={Link}
              to="/dashboard"
              sx={{
                display: { xs: 'flex', sm: 'none' },
                '&:hover': { backgroundColor: colors.primary[800] },
              }}
            >
              <DashboardIcon />
            </IconButton>

            <IconButton
              color="inherit"
              onClick={onToggleTheme}
              aria-label={themeMode === 'dark' ? t('header.switchToLightMode') : t('header.switchToDarkMode')}
              title={themeMode === 'dark' ? t('header.switchToLightMode') : t('header.switchToDarkMode')}
              sx={{ '&:hover': { backgroundColor: colors.primary[800] } }}
            >
              {themeMode === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>

            <IconButton
              color="inherit"
              onClick={handleHelpModalOpen}
              aria-label={t('header.onboardingAriaLabel')}
              data-tour="header-tour-trigger"
              sx={{ '&:hover': { backgroundColor: colors.primary[800] } }}
            >
              <HelpOutline />
            </IconButton>

            {tier && (
              <Chip
                label={tier.label}
                aria-label={t('header.currentPlan', { plan: tier.label })}
                size="small"
                component={Link}
                to="/mis-licencias"
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  height: 28,
                  maxWidth: { sm: 150, md: 190 },
                  border: `1px solid ${alpha(tier.color, 0.35)}`,
                  backgroundColor: tier.bg,
                  color: tier.color,
                  fontWeight: 800,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            )}

            <Stack
              direction="row"
              sx={{
                borderRadius: '999px',
                border: `1px solid ${alpha(colors.neutral.white, 0.12)}`,
                overflow: 'hidden',
                background: alpha(colors.neutral.white, 0.04),
              }}
            >
              {['es', 'en'].map((lang) => {
                const currentLang = (i18n.resolvedLanguage || i18n.language || 'es').substring(0, 2);
                const isActive = currentLang === lang;
                return (
                  <Button
                    key={lang}
                    onClick={() => i18n.changeLanguage(lang)}
                    disableRipple
                    size="small"
                    sx={{
                      minWidth: 0,
                      px: 1.5,
                      py: 0.3,
                      borderRadius: '999px',
                      fontSize: '0.68rem',
                      fontWeight: isActive ? 800 : 500,
                      letterSpacing: '0.06em',
                      color: isActive ? colors.primary[900] : alpha(colors.neutral.white, 0.55),
                      background: isActive ? colors.secondary[400] : 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: isActive ? colors.primary[900] : colors.neutral.white,
                        background: isActive ? colors.secondary[400] : alpha(colors.neutral.white, 0.08),
                      },
                    }}
                  >
                    {lang.toUpperCase()}
                  </Button>
                );
              })}
            </Stack>



            <IconButton
              onClick={handleMenuOpen}
              aria-label={t('header.userMenuAriaLabel')}
              sx={{
                p: 0.5,
                '&:hover': { backgroundColor: colors.primary[800] },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: colors.secondary[500],
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {getUserInitials(usuario?.nombre)}
              </Avatar>
            </IconButton>

            <MuiMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 220,
                  boxShadow: shadows.dropdown,
                  borderRadius: 0,
                },
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  // Fixed light-green tint in light mode; switch to a
                  // translucent brand tint in dark mode so the theme-aware
                  // text below (subtitle2 default + text.secondary caption)
                  // stays legible instead of light-on-light.
                  backgroundColor: themeMode === 'dark' ? alpha(colors.primary[500], 0.16) : colors.primary[50],
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  {usuario?.nombre || t('common:roles.user')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color={'text.secondary'}>
                    {usuario?.email || getRoleLabel(usuario?.rol, t) || t('common:roles.user')}
                  </Typography>
                  {tier && (
                    <Chip
                      label={tier.label}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        backgroundColor: tier.bg,
                        color: tier.color,
                      }}
                    />
                  )}
                </Box>
              </Box>

              <Divider />

              <UserMenuItem onClick={handleMenuClose} component={Link} to="/dashboard">
                <DashboardIcon sx={{ mr: 2, fontSize: 20 }} />
                {t('menuItems.dashboard')}
              </UserMenuItem>

              <UserMenuItem onClick={handleMenuClose} component={Link} to="/perfil">
                <Person sx={{ mr: 2, fontSize: 20 }} />
                {t('header.profile')}
              </UserMenuItem>

              <UserMenuItem onClick={handleMenuClose} component={Link} to="/cliente-windows">
                <DesktopWindows sx={{ mr: 2, fontSize: 20 }} />
                {t('menuItems.windowsApp')}
              </UserMenuItem>

              {usuario?.rol === 'admin' && [
                  <UserMenuItem key="admin" onClick={handleMenuClose} component={Link} to="/admin/analytics">
                    <AdminPanelSettings sx={{ mr: 2, fontSize: 20 }} />
                    {t('header.adminPanel')}
                  </UserMenuItem>,
                  <UserMenuItem key="versiones" onClick={handleMenuClose} component={Link} to="/admin/versiones">
                    <SystemUpdateAlt sx={{ mr: 2, fontSize: 20 }} />
                    Versiones
                  </UserMenuItem>,
                ]}

              <Divider />

              <UserMenuItem
                onClick={() => {
                  handleMenuClose();
                  onLogout();
                }}
                sx={{ color: colors.error.main }}
              >
                <ExitToApp sx={{ mr: 2, fontSize: 20 }} />
                {t('header.logout')}
              </UserMenuItem>
            </MuiMenu>

          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              color="inherit"
              onClick={handleHelpModalOpen}
              aria-label={t('header.onboardingAriaLabel')}
              data-tour="header-tour-trigger"
              sx={{ '&:hover': { backgroundColor: colors.primary[800] } }}
            >
              <HelpOutline />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={onLogin}
              aria-label={t('header.loginAriaLabel')}
              sx={{
                backgroundColor: colors.secondary[500],
                // Fixed dark text: this button always sits on a brand-yellow
                // surface regardless of theme mode, so it must not follow
                // theme.palette.text.primary (which turns light in dark mode).
                color: colors.text.primary,
                fontWeight: 700,
                px: 3,
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: colors.secondary[600],
                },
              }}
            >
              <Person sx={{ mr: 1 }} />
              <Typography variant="button" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {t('header.login')}
              </Typography>
            </IconButton>
          </Box>
        )}

        <HelpModal
          open={helpModalOpen}
          onClose={handleHelpModalClose}
          onExited={handleHelpModalExited}
          onStartTour={handleStartTour}
          currentPage={resolvedHelpPage}
          hasPageTour={hasPageTour}
        />
      </Toolbar>
    </StyledAppBar>
  );
};

export default ModernHeader;
